import { useEffect, useState, type ReactNode } from 'react'

import { cn } from '../lib/utils'
import { SidebarTrigger } from '../components/sidebar'

type HeaderProps = React.HTMLAttributes<HTMLElement> & {
  fixed?: boolean
  center?: ReactNode
  end?: ReactNode
  ref?: React.Ref<HTMLElement>
}

export function Header({
  className,
  fixed,
  center,
  end,
  children,
  ...props
}: HeaderProps) {
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = (event: Event) => {
      const target = event.target
      if (target instanceof HTMLElement) {
        if (target.dataset.slot === 'sidebar-inset')
          setScrolled(target.scrollTop > 10)
        return
      }
      setScrolled(document.documentElement.scrollTop > 10)
    }
    document.addEventListener('scroll', onScroll, {
      passive: true,
      capture: true,
    })
    return () =>
      document.removeEventListener('scroll', onScroll, { capture: true })
  }, [])

  return (
    <header
      className={cn(
        'z-30 h-14 shrink-0 rounded-t-[inherit] bg-background/95',
        fixed && 'header-fixed peer/header sticky top-0 w-[inherit]',
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          'relative grid h-full grid-cols-[1fr_auto] items-center gap-3 px-4 md:grid-cols-[1fr_auto_1fr]',
          scrolled &&
            fixed &&
            'after:absolute after:inset-0 after:-z-10 after:bg-background/75 after:backdrop-blur-xl',
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <SidebarTrigger className="rounded-full text-muted-foreground" />
          {children}
        </div>
        <div className="hidden justify-center md:flex">{center}</div>
        <div className="flex items-center justify-end gap-0.5">{end}</div>
      </div>
    </header>
  )
}
